/**
 * columnExportAttrs.ts — atributos de EXPORTACIÓN (Excel) por columna. PT-#70.
 *
 * Modela title / format / widthChars por columna, con una cadena de resolución de
 * 2 capas sobre un default de código:
 *
 *     template.override ?? columnConfig.export ?? DEFAULT_EXPORT_ATTRS[id] ?? hardcoded
 *
 * IMPORTANTE — separación de responsabilidades:
 * - Estos atributos son SOLO para el Excel exportado (numFmt, ancho de columna en
 *   caracteres, título de header). NO tienen relación con el `format?: FormatType`
 *   de columnConfig (src/utils/columnFormatters.ts), que gobierna el render EN PANTALLA
 *   de la celda. El formato on-screen "símil excel" es trabajo futuro (PT-#71).
 * - Los defaults salen del reporte real de Samsung (export_10tomas 2026-08-21): 23
 *   columnas con su title/format/ancho observados en el XLSX. Se seedean por id LÓGICO
 *   (el mismo id que produce buildExportColumns / las leaf columns de PUBS), NO por id
 *   físico ni por header.
 *
 * Módulo puro (sin I/O) → unit-testeable. Lo consumen ambos paths de export:
 *   - PUBS  → src/utils/exportToExcel.ts (xlsx-js-style, cell.z + !cols)
 *   - EXPORTAR → src/utils/exportColumns.ts + server/routes/exportRoutes.js (exceljs stream)
 */

// ---- Taxonomía de formato de EXCEL (distinta de FormatType on-screen) ----
export type ExcelExportFormat =
  | 'text'
  | 'integer'
  | 'number'
  | 'percent'
  | 'currency_ars'
  | 'date'
  | 'week'
  | 'hyperlink';

/**
 * numFmt de Excel por formato. `null` = sin numFmt (celda string / manejada aparte):
 * - date  → string DD/MM/YYYY (se formatea antes de escribir la celda)
 * - week  → string AAAA-Www
 * - hyperlink → fórmula HYPERLINK() (sin numFmt, estilo link)
 * - text  → general (string)
 * currency_ars usa LCID es-AR [$-2C0A] para forzar separadores . miles / , decimales
 * independiente del locale del sistema (mismo formato que PRICE_FORMAT existente).
 * percent usa `0%`: Excel multiplica ×100 al mostrar, así que el VALOR de la celda
 * debe guardarse como fracción (40% → 0.4). Ver toPercentValue().
 */
export const EXCEL_NUMFMT: Record<ExcelExportFormat, string | null> = {
  text: null,
  integer: '0',
  number: '#,##0.##',
  percent: '0%',
  currency_ars: '[$-2C0A]"$"#,##0.00',
  date: null,
  week: null,
  hyperlink: null,
};

export function excelNumFmtFor(format: ExcelExportFormat | undefined | null): string | null {
  if (!format) return null;
  return EXCEL_NUMFMT[format] ?? null;
}

// ---- Tipo del bloque de atributos de export ----
export interface ExportAttrs {
  /** Título del header en el Excel (sobrescribe el displayName del registro). */
  title?: string;
  /** Formato de celda de Excel. */
  format?: ExcelExportFormat;
  /** Ancho de columna en CARACTERES (unidad nativa de Excel; px no aplica). */
  widthChars?: number;
}

/** Atributos resueltos (todos los campos presentes). */
export interface ResolvedExportAttrs {
  title: string;
  format: ExcelExportFormat;
  widthChars: number;
}

/**
 * DEFAULT por id lógico, seedeado del reporte Samsung real (23 columnas).
 * width en chars = valor observado en el XLSX (MDW=7, Calibri 11).
 */
export const DEFAULT_EXPORT_ATTRS: Record<string, ExportAttrs> = {
  // Calculadas / inyectadas
  toma: { title: 'Toma', format: 'text', widthChars: 12 },
  semana: { title: 'Semana', format: 'week', widthChars: 12 },
  id_pulpou: { title: 'ID Pulpou', format: 'integer', widthChars: 12 },
  // url/imagen: hyperlink DESHABILITADO por ahora (format 'text') — ahorra espacio (la
  // fórmula HYPERLINK() duplica la URL en la celda) y elimina la inconsistencia del cap de
  // 255 chars de Excel (URLs largas de imagen quedaban en texto plano y las cortas con link).
  // El switch hyperlink yes/no por columna y por template va como feature futura (ver GHI).
  url: { title: 'URL', format: 'text', widthChars: 35 },
  // Core / native
  fecha: { title: 'Fecha', format: 'date', widthChars: 13.71 },
  sku: { title: 'SKU Unificado', format: 'text', widthChars: 29.29 },
  marca: { title: 'Marca', format: 'text', widthChars: 13.43 },
  titulo: { title: 'Titulo', format: 'text', widthChars: 51.29 },
  sitio: { title: 'Sitio', format: 'text', widthChars: 19 },
  precio: { title: 'Precio Base', format: 'currency_ars', widthChars: 17.14 },
  precio_desc: { title: 'Precio Desc', format: 'currency_ars', widthChars: 17.14 },
  precio_venta: { title: 'Precio Venta', format: 'currency_ars', widthChars: 17.14 },
  descuento: { title: '% Desc', format: 'percent', widthChars: 10 },
  cuotas: { title: 'Cuotas', format: 'integer', widthChars: 10 },
  precio_cuota: { title: 'Valor Cuota', format: 'currency_ars', widthChars: 17.14 },
  categoria: { title: 'Categoría', format: 'text', widthChars: 25.71 },
  subcategory: { title: 'Subcategoría', format: 'text', widthChars: 25.71 },
  segmento: { title: 'Segmento', format: 'text', widthChars: 25.71 },
  sku_origen: { title: 'SKU Origen', format: 'text', widthChars: 29.29 },
  sku_unificado: { title: 'SKU Unificado', format: 'text', widthChars: 29.29 },
  imagen: { title: 'Imagen', format: 'text', widthChars: 26.43 }, // hyperlink deshabilitado (ver nota en `url`)
  udm: { title: 'udm', format: 'text', widthChars: 12 },
  contenido: { title: 'contenido', format: 'text', widthChars: 12 },
  pagina_orden: { title: 'Posicion', format: 'text', widthChars: 12 },
  // PT-#80: Vendedor (seller de MELI, catalog-only). Ancho similar a categoría (nombres de
  // tienda oficial pueden ser largos). No entra en DATA_EXPORT_BASE_ORDER (reporte base 24 cols).
  seller: { title: 'Vendedor', format: 'text', widthChars: 25.71 },
};

/**
 * Alias id FÍSICO/NATIVO → id LÓGICO de export. Los DEFAULT_EXPORT_ATTRS se seedean por id
 * lógico (español: `precio_venta`, `descuento`, `segmento`, `udm`), pero las columnas vivas
 * llegan con su id físico/nativo: PUBS con el `column.Field` del schema (`sale_price`,
 * `discount`), EXPORTAR con la clave nativa del columnConfig (`discount`, `segment`,
 * `udm_source`). Sin este alias, `discount` NO resolvía a `descuento` → salía title
 * 'Descuento'/format text y el valor 17 como entero en vez de 0.17 con numFmt 0% (PT-#70 #4).
 * Lo consumen AMBOS paths (exportToExcel.ts y ExportPanel.tsx) vía `toLogicalExportId`.
 */
export const PHYSICAL_TO_LOGICAL_ID: Record<string, string> = {
  sale_price: 'precio_venta',
  original_price: 'precio',
  discounted_price: 'precio_desc',
  discount: 'descuento',
  segment: 'segmento',
  udm_source: 'udm',
};

/** Normaliza un id físico/nativo (lowercased) a su id lógico de export. */
export function toLogicalExportId(id: string): string {
  const lc = String(id || '').toLowerCase();
  return PHYSICAL_TO_LOGICAL_ID[lc] || lc;
}

/** Orden canónico de las 23 columnas del reporte base data_export (PT-#70). */
export const DATA_EXPORT_BASE_ORDER: string[] = [
  'fecha', 'toma', 'semana', 'sku', 'id_pulpou', 'marca', 'titulo', 'sitio', 'url',
  'precio', 'precio_desc', 'precio_venta', 'descuento', 'cuotas', 'precio_cuota',
  'categoria', 'subcategory', 'segmento', 'sku_origen', 'sku_unificado', 'imagen', 'udm', 'contenido',
  'pagina_orden',
];

const HARDCODED_FALLBACK: ResolvedExportAttrs = { title: '', format: 'text', widthChars: 12 };

/**
 * Fallback defensivo por título cuando el id lógico no está en DEFAULT_EXPORT_ATTRS.
 * Cubre sobre todo el caso percent (% Desc): si un cliente tiene la columna de
 * descuento con otro id, igual sale como porcentaje. Espeja el estilo de las regex
 * de header ya existentes (INTEGER_HEADER_REGEX / PRICE_COL_REGEX).
 */
function guessFormatByTitle(title?: string): ExcelExportFormat | undefined {
  if (!title) return undefined;
  const t = title.trim().toLowerCase();
  if (/%|porcentaje|desc\.?$|^%/.test(t) && /desc|%/.test(t)) return 'percent';
  if (/^cuotas$/.test(t)) return 'integer';
  if (/precio|cuota/.test(t)) return 'currency_ars';
  if (/^(imagen|url|link)$/.test(t)) return 'hyperlink';
  if (/fecha/.test(t)) return 'date';
  if (/semana/.test(t)) return 'week';
  return undefined;
}

export interface ResolveExportAttrsInput {
  /** Override a nivel template (bloque `export` del item del template). Read-only por ahora. */
  templateExport?: ExportAttrs | null;
  /** Default del cliente (bloque `export` de la entry en columnConfig core/native). */
  columnConfigExport?: ExportAttrs | null;
  /** Header/displayName vigente — se usa como title fallback y para guessFormatByTitle. */
  fallbackTitle?: string;
}

/**
 * Resuelve los atributos de export para una columna.
 * Cadena por campo: template.override ?? columnConfig.export ?? DEFAULT[id] ?? hardcoded.
 * `title` cae además a fallbackTitle (displayName) y por último al id lógico.
 */
export function resolveExportAttrs(
  logicalId: string,
  input: ResolveExportAttrsInput = {}
): ResolvedExportAttrs {
  const tpl = input.templateExport || undefined;
  const cc = input.columnConfigExport || undefined;
  const def = DEFAULT_EXPORT_ATTRS[logicalId];

  const title =
    tpl?.title ?? cc?.title ?? def?.title ?? input.fallbackTitle ?? logicalId;

  const format =
    tpl?.format ??
    cc?.format ??
    def?.format ??
    guessFormatByTitle(def?.title ?? input.fallbackTitle) ??
    HARDCODED_FALLBACK.format;

  const widthChars =
    tpl?.widthChars ?? cc?.widthChars ?? def?.widthChars ?? HARDCODED_FALLBACK.widthChars;

  return { title, format, widthChars };
}

/**
 * Convierte un valor de porcentaje "humano" (40 = 40%) a la fracción que Excel
 * espera con numFmt `0%` (0.4). Acepta number o string ("40", "40%", "40,0").
 * Devuelve null si no es numérico (celda vacía).
 */
export function toPercentValue(raw: any): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return isFinite(raw) ? raw / 100 : null;
  const s = String(raw).replace(/%/g, '').replace(',', '.').trim();
  if (!s) return null;
  const n = parseFloat(s);
  return isFinite(n) ? n / 100 : null;
}
